// /Users/pramod/Desktop/school-erp/frontend/src/Tenant-Admin/WebsiteManager/common/ImagePreview.js

import React, { useState, useEffect, useRef } from 'react';
import { FiImage } from 'react-icons/fi';

const buildResolvedSrc = (src) => {
  // Use REACT_APP_BACKEND_URL or fallback to window.location.origin
  const base = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/+$/, '');

  if (!src) return '';

  // If src is an object with url or fileId
  if (typeof src === 'object') {
    if (src.url) {
      src = src.url;
    } else if (src.fileId) {
      return `${base}/api/files/tenant-website/${src.fileId}`;
    } else {
      return '';
    }
  }

  // If src is a string, clean it up
  src = src.trim();

  // If src is empty after trimming
  if (!src) return '';

  // --- CHECK: If src already has a protocol, return as-is ---
  if (/^https?:\/\//i.test(src)) {
    // URL already has http:// or https://, don't modify
    return src;
  }

  // --- CHECK: If src starts with // (protocol-relative) ---
  if (src.startsWith('//')) {
    // Add the current protocol (http or https)
    const protocol = window.location.protocol || 'https:';
    return `${protocol}${src}`;
  }

  // --- Handle API routes ---
  // Check if it's a tenant-website file route
  if (src.includes('/api/files/tenant-website/')) {
    if (src.startsWith('/api/files/tenant-website/')) {
      return `${base}${src}`;
    }
    return src;
  }

  // Check if it's a student profile route
  if (src.includes('/api/files/student-profile/')) {
    if (src.startsWith('/api/files/student-profile/')) {
      return `${base}${src}`;
    }
    return src;
  }

  // Generic file route
  if (src.startsWith('/api/files/')) {
    return `${base}${src}`;
  }

  // If it's just a fileId (MongoDB ObjectId format)
  if (/^[a-f\d]{24}$/i.test(src)) {
    return `${base}/api/files/tenant-website/${src}`;
  }

  // relative path with slash -> prepend base
  if (src.startsWith('/')) {
    return `${base}${src}`;
  }

  // If it doesn't start with / and doesn't have a protocol, treat as relative
  if (!src.startsWith('/') && !src.includes('://')) {
    return `${base}/${src}`;
  }

  return src;
};

const ImagePreview = ({ src, alt, className = '', isLogo = false }) => {
  const resolvedSrc = buildResolvedSrc(src);
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const imgRef = useRef(null);
  const lastSrcRef = useRef(null);

  // Reset states when src changes
  useEffect(() => {
    // Reset states when resolvedSrc changes
    setLoaded(false);
    setErrored(false);
    setIsLoading(true);
    lastSrcRef.current = resolvedSrc;

    if (!resolvedSrc) {
      setLoaded(false);
      setErrored(true);
      setIsLoading(false);
      return;
    }

    // Create image for preloading
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      setLoaded(true);
      setErrored(false);
      setIsLoading(false);
    };
    
    img.onerror = () => {
      console.warn('Failed to load image:', resolvedSrc);
      setLoaded(false);
      setErrored(true);
      setIsLoading(false);
    };
    
    img.src = resolvedSrc;

    // Cleanup
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [resolvedSrc]);

  const logoStyle = isLogo
    ? { width: '120px', height: '80px', margin: '0 auto' }
    : {};

  // For logo images, use different container styling
  if (isLogo) {
    return (
      <div
        className={`relative overflow-hidden rounded-lg bg-white ${className}`}
        style={logoStyle}
      >
        {resolvedSrc && !errored ? (
          <img
            ref={imgRef}
            src={resolvedSrc}
            alt={alt || 'Image'}
            className="w-full h-full object-contain"
            onLoad={() => {
              setLoaded(true);
              setIsLoading(false);
            }}
            onError={(e) => {
              console.warn('Logo image failed to load:', resolvedSrc);
              setErrored(true);
              setIsLoading(false);
            }}
            loading="lazy"
          />
        ) : (
          <div className="h-full bg-gray-100 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg">
            <div className="text-center px-3">
              <FiImage className="text-gray-400 text-2xl mx-auto mb-2" />
              <span className="text-sm text-gray-500 block">
                {errored ? 'Failed to load' : 'No image'}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Regular image with aspect ratio
  return (
    <div
      className={`relative overflow-hidden rounded-lg bg-white ${className}`}
      style={{ paddingBottom: '56.25%' }}
    >
      {resolvedSrc && !errored ? (
        <>
          <img
            ref={imgRef}
            src={resolvedSrc}
            alt={alt || 'Image'}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
            style={{
              opacity: isLoading || !loaded ? 0 : 1,
            }}
            onLoad={() => {
              setLoaded(true);
              setIsLoading(false);
            }}
            onError={(e) => {
              console.warn('Image failed to load:', resolvedSrc);
              setErrored(true);
              setIsLoading(false);
            }}
            loading="lazy"
          />
          {(isLoading || !loaded) && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          )}
        </>
      ) : (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg">
          <div className="text-center px-3">
            <FiImage className="text-gray-400 text-2xl mx-auto mb-2" />
            <span className="text-sm text-gray-500 block">
              {errored ? 'Failed to load' : 'No image'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImagePreview;